import { IWatrRequest } from '@nkr/models/index';
import { WageAndTravelCategory, WageAndTravelStatus, WageAndTravelType } from '@nkr/enums/index';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { getDefaultWeeklyWageBase } from '../common/wage-computation';
import {
    resolveDerProgramCap,
    isIntlWorkupReimbursementProgram,
    isPostDonationDerPurse,
    readIsInternationallyEligibleForWatr,
    showIntlPostWorkupProgramStatus,
} from '../advanced-reimbursement-status-buckets';

export const WEEKLY_CAP = 2000;

export type DerVerifyDisplayAmounts = {
    totalAmount: number;
    pendingAmount: number;
};

/** Intl AR payment rows on Pend ACH use the same LWR/DER/Pending totals as Pend Wire. */
export function usesPendWireAmountLogicOnPendAch(data: IWatrRequest | undefined | null): boolean {
    if (!data) {
        return false;
    }
    return readIsInternationallyEligibleForWatr(data)
        && !data.donationConfirmed
        && (data.status === WageAndTravelStatus.PendingAdvancedReimbursement
            || !!data.advancedProgress);
}

const isValidDateValue = (value: unknown): boolean => {
    if (value == null) {
        return false;
    }
    const timestamp = new Date(value as string | number | Date).getTime();
    return !isNaN(timestamp);
};

export function hasPriorDerReimbursement(data: IWatrRequest | undefined | null): boolean {
    if (!data) {
        return false;
    }

    if (data.hasBeenTravelReimbursed) {
        return true;
    }

    const priorStatuses = [
        WageAndTravelReimbursementRequestStatus.AdminApproved,
        WageAndTravelReimbursementRequestStatus.Reimbursed,
    ];

    const travels = data.requestTravels || [];
    return travels.some(travel =>
        priorStatuses.indexOf(travel.status) >= 0
        || (travel.previousReimbursementAmount || 0) > 0
    );
}

export const getDerVerifyDisplayAmounts = (data: IWatrRequest): DerVerifyDisplayAmounts => {
    if (!data) {
        return { totalAmount: 0, pendingAmount: 0 };
    }

    const travels = data.requestTravels || [];
    const pendingTravels = travels.filter(x =>
        x.status === WageAndTravelReimbursementRequestStatus.PendingApproval
    );

    const pendingAmount = pendingTravels.reduce((sum, travel) => sum + (travel.travelReceiptsTotal || 0), 0);
    const previouslyPaidAmount = travels.reduce((max, travel) => {
        const paid = travel.previousReimbursementAmount || 0;
        return paid > max ? paid : max;
    }, 0);

    const derLimit = resolveDerProgramCap(data);
    const limit = derLimit != null && derLimit > 0 ? derLimit : 0;
    const remainingRoom = limit > 0 ? Math.max(0, limit - previouslyPaidAmount) : null;

    let totalAmount = 0;
    if (pendingAmount > 0) {
        totalAmount = remainingRoom != null
            ? Math.min(pendingAmount, remainingRoom)
            : pendingAmount;
    } else {
        const allowedPending = pendingTravels.reduce(
            (sum, travel) => sum + (travel.allowedReimbursementAmount || 0),
            0
        );
        if (allowedPending > 0) {
            totalAmount = remainingRoom != null
                ? Math.min(allowedPending, remainingRoom)
                : allowedPending;
        }
    }

    return {
        totalAmount: isNaN(totalAmount) ? 0 : totalAmount,
        pendingAmount: isNaN(pendingAmount) ? 0 : pendingAmount
    };
};

export function applyIntlPostWorkupVisitCap(amount: number, data: IWatrRequest): number {
    const value = amount || 0;
    if (!data) {
        return value;
    }

    if (showIntlPostWorkupProgramStatus(data)) {
        const visitCap = data.arInternationalWorkupVisitCap;

        if (visitCap == null || visitCap <= 0) {
            return value;
        }

        return Math.min(value, visitCap);
    }

    if (isPostDonationDerPurse(data) && isIntlWorkupReimbursementProgram(data)) {
        const derLimit = resolveDerProgramCap(data);

        if (derLimit <= 0) {
            return value;
        }

        return Math.min(value, derLimit);
    }

    return value;
}

export function getSearchDerReimbursedStandard(data: IWatrRequest | undefined | null): number {
    if (!data) {
        return 0;
    }
    const amount = data.derReimbursedStandard;
    if (amount != null && !isNaN(amount)) {
        return amount;
    }
    return 0;
}

export function getDerColumnDisplayAmount(
    data: IWatrRequest,
    selectedTab: WageAndTravelCategory
): number {
    if (!data) {
        return 0;
    }

    if (selectedTab === WageAndTravelCategory.PendingDERVerify) {
        return getDerVerifyDisplayAmounts(data).totalAmount;
    }

    if (selectedTab === WageAndTravelCategory.Search) {
        return getSearchDerReimbursedStandard(data);
    }

    if (selectedTab === WageAndTravelCategory.PendingPayment) {
        if (usesPendWireAmountLogicOnPendAch(data)) {
            return applyIntlPostWorkupVisitCap(data.tlr || 0, data);
        }
        if (data.achReimbursementType !== WageAndTravelType.ReimburseTravelOnly) {
            return 0;
        }
        return applyIntlPostWorkupVisitCap(data.transactionAmount || 0, data);
    }

    return applyIntlPostWorkupVisitCap(data.tlr || 0, data);
}

export const getBaseWeeklyAmount = (data: IWatrRequest): number => getDefaultWeeklyWageBase(data);

export const getEligibleLwrWeekCount = (data: IWatrRequest): number => {
    if (!data || !data.requestWeeks) {
        return 0;
    }

    return data.requestWeeks.filter(x =>
        x.status > WageAndTravelReimbursementRequestStatus.PendingApproval &&
        x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined
    ).length;
};

export const getLwrWeeklyDisplayAmount = (data: IWatrRequest): number => {
    const baseAmount = getBaseWeeklyAmount(data);
    const cappedBaseAmount = Math.min(baseAmount, WEEKLY_CAP);
    const roundedBaseAmount = Math.round(cappedBaseAmount * 100) / 100;
    const incomePercentCovered = data.incomePercentCovered || 0;
    return calculateAdjustedBaseAmount(roundedBaseAmount, incomePercentCovered, WEEKLY_CAP);
};

export const calculateAdjustedBaseAmount = (baseAmount: number, incomePercentCovered: number, weeklyCap: number): number => {
    if (!baseAmount || isNaN(baseAmount) || baseAmount <= 0) {
        return 0;
    }

    let adjusted = baseAmount;

    if (incomePercentCovered > 0) {
        const percentToCover = 100 - incomePercentCovered;
        adjusted = Math.round((baseAmount * (percentToCover / 100)) * 100) / 100;
    }

    const capped = Math.min(adjusted, weeklyCap);
    return isNaN(capped) ? 0 : capped;
};

const calculateAdjustedWeeklyReimbursement = (adjustedBaseAmount: number, otherAmount: number, weeklyCap: number): number => {
    if (!adjustedBaseAmount || isNaN(adjustedBaseAmount)) {
        return 0;
    }

    const other = otherAmount || 0;
    const result = Math.max(0, Math.round((adjustedBaseAmount - other) * 100) / 100);
    const cappedResult = Math.min(result, weeklyCap);
    return isNaN(cappedResult) ? 0 : cappedResult;
};

export const calculateLwrTotal = (data: IWatrRequest): number => {
    if (!data || !data.requestWeeks) {
        return 0;
    }

    const eligibleWeeks = data.requestWeeks.filter(x =>
        x.status > WageAndTravelReimbursementRequestStatus.PendingApproval &&
        x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined
    ) || [];

    if (eligibleWeeks.length === 0) {
        return 0;
    }

    const baseAmount = getBaseWeeklyAmount(data);
    const cappedBaseAmount = Math.min(baseAmount, WEEKLY_CAP);
    const roundedBaseAmount = Math.round(cappedBaseAmount * 100) / 100;
    const incomePercentCovered = data.incomePercentCovered || 0;
    const adjustedBaseAmount = calculateAdjustedBaseAmount(roundedBaseAmount, incomePercentCovered, WEEKLY_CAP);

    return eligibleWeeks.reduce((sum, week) => {
        const otherAmount = week.otherAmount || 0;
        const adjustedAmount = calculateAdjustedWeeklyReimbursement(adjustedBaseAmount, otherAmount, WEEKLY_CAP);
        return sum + adjustedAmount;
    }, 0);
};

const groupedTabsForHeaderTotals: WageAndTravelCategory[] = [
    WageAndTravelCategory.PendingWire,
    WageAndTravelCategory.PaymentError,
    WageAndTravelCategory.PendSurgery,
    WageAndTravelCategory.PendingDERVerify,
    WageAndTravelCategory.PendingLWRVerify
];

function getPendWireStyleRowTotals(data: IWatrRequest): { lwrTotal: number; derTotal: number; transactionTotal: number } {
    const weekLwrTotal = calculateLwrTotal(data);
    const der = applyIntlPostWorkupVisitCap(data.tlr || 0, data);
    return {
        lwrTotal: weekLwrTotal,
        derTotal: der,
        transactionTotal: der + weekLwrTotal
    };
}

export function getRowTotalsForPendingHeader(
    data: IWatrRequest,
    selectedTab: WageAndTravelCategory
): { lwrTotal: number; derTotal: number; transactionTotal: number } {
    if (selectedTab === WageAndTravelCategory.PendingPayment) {
        if (usesPendWireAmountLogicOnPendAch(data)) {
            return getPendWireStyleRowTotals(data);
        }
        let weekLwrTotal = 0;
        if (data.achReimbursementType === WageAndTravelType.ReimburseWagesOnly) {
            weekLwrTotal = calculateLwrTotal(data);
            return { lwrTotal: weekLwrTotal, derTotal: 0, transactionTotal: weekLwrTotal };
        }
        const derTotal = getDerColumnDisplayAmount(data, selectedTab);
        return { lwrTotal: 0, derTotal, transactionTotal: derTotal };
    }

    if (groupedTabsForHeaderTotals.includes(selectedTab)) {
        const weekLwrTotal = calculateLwrTotal(data);
        const pendingSurgery = selectedTab === WageAndTravelCategory.PendSurgery;
        if (pendingSurgery && !isValidDateValue(data.donationDate)) {
            return { lwrTotal: 0, derTotal: 0, transactionTotal: 0 };
        }
        const der = getDerColumnDisplayAmount(data, selectedTab);
        const travelForPending = applyIntlPostWorkupVisitCap(data.tlr || 0, data);
        return {
            lwrTotal: weekLwrTotal,
            derTotal: der,
            transactionTotal: travelForPending + weekLwrTotal
        };
    }

    return { lwrTotal: 0, derTotal: 0, transactionTotal: 0 };
}

export function pendingColumnShowsTransactionHeaderTotal(
    selectedTab: WageAndTravelCategory,
    isSuperUser: boolean
): boolean {
    if (
        selectedTab === WageAndTravelCategory.PendingPayment ||
        selectedTab === WageAndTravelCategory.PendingWire ||
        selectedTab === WageAndTravelCategory.PaymentError
    ) {
        return true;
    }
    if (isSuperUser && selectedTab === WageAndTravelCategory.PendSurgery) {
        return true;
    }
    return false;
}
